import {
  type AddHighlightOptions,
  type HighlightMode,
  type HighlightRange,
  type HighlightsController,
  type HighlightsRenderer
} from '@databases/highlights';
import {
  HIGHLIGHT_FIXED_FRAGMENT_OPEN_END_CLASS,
  HIGHLIGHT_SCROLLING_FRAGMENT_OPEN_START_CLASS
} from '@databases/highlights/constants';

import { FIXED_HIGHLIGHTS_SURFACE_Z_INDEX } from './constants';

const PINNED_LEADING_BOUNDARY_ATTRIBUTE = 'data-dbs-pinned-leading-freeze-boundary';
const PINNED_LEADING_BOUNDARY_SELECTOR = `[${PINNED_LEADING_BOUNDARY_ATTRIBUTE}="true"][data-col-index]`;
const FIXED_FRAGMENT_ID_SUFFIX = ':fixed-horizontal';

export interface SelectionHighlightsLayerOptions {
  scrollingZIndex: number;
  scrollContainer: HTMLElement;
}

/**
 * Presents one logical selection-highlight layer to selection effects while
 * coordinating the physical renderers needed by the table layout.
 *
 * `HighlightsController` creates physical layers and `HighlightsRenderer`
 * draws a range in one coordinate space. This class composes those primitives:
 * it always owns a scrolling renderer and lazily creates a horizontally-fixed
 * renderer when a cell or column range intersects the fixed columns.
 * A range wholly within one region is sent to that region's renderer; a range
 * crossing the boundary is split into fixed and scrolling fragments that are
 * updated and removed together.
 *
 * Selection state remains a single index-based range. This class derives its
 * physical projection from the current pinned-column DOM layout, independently
 * of whether the selection originated from pointer, keyboard, menu, or
 * programmatic input. It does not capture input or mutate selection state.
 */
export class SelectionHighlightsLayer {
  readonly #controller: HighlightsController;
  readonly #scrollingLayer: HighlightsRenderer;
  readonly #scrollingZIndex: number;
  readonly #scrollContainer: HTMLElement;

  #fixedLayer: HighlightsRenderer | null = null;
  #activeHighlights = new Map<string, AddHighlightOptions>();
  #layoutObserver: MutationObserver | null = null;
  #disposed = false;

  constructor(
    controller: HighlightsController,
    options: SelectionHighlightsLayerOptions
  ) {
    this.#controller = controller;
    this.#scrollingZIndex = options.scrollingZIndex;
    this.#scrollContainer = options.scrollContainer;
    this.#scrollingLayer = controller.createLayer();
    this.#layoutObserver = new MutationObserver((mutations) => {
      if (
        mutations.some(
          (mutation) =>
            mutation.type === 'attributes' &&
            mutation.attributeName === PINNED_LEADING_BOUNDARY_ATTRIBUTE
        )
      ) {
        this.#reprojectAll();
      }
    });
    this.#layoutObserver.observe(this.#scrollContainer, {
      attributes: true,
      subtree: true,
      attributeFilter: [PINNED_LEADING_BOUNDARY_ATTRIBUTE]
    });
  }

  add(options: AddHighlightOptions): HTMLElement {
    this.#activeHighlights.set(options.range.id, options);

    return this.#sync(options);
  }

  update(range: HighlightRange, mode: HighlightMode) {
    const active = this.#activeHighlights.get(range.id);

    if (!active) {
      return;
    }

    this.#activeHighlights.set(range.id, { ...active, range, mode });

    if (this.#getFixedColumnCount() === 0) {
      this.#scrollingLayer.update(range, mode);

      return;
    }

    this.#sync({ ...active, range, mode });
  }

  remove(id: string) {
    this.#activeHighlights.delete(id);
    this.#scrollingLayer.remove(id);
    this.#fixedLayer?.remove(id);
    this.#fixedLayer?.remove(this.#fixedFragmentId(id));
  }

  clear() {
    this.#activeHighlights.clear();
    this.#scrollingLayer.clear();
    this.#fixedLayer?.clear();
  }

  hideAll() {
    this.#scrollingLayer.hideAll();
    this.#fixedLayer?.hideAll();
  }

  showAll() {
    this.#scrollingLayer.showAll();
    this.#fixedLayer?.showAll();
  }

  redrawAll() {
    this.#scrollingLayer.redrawAll();
    this.#fixedLayer?.redrawAll();
  }

  #reprojectAll() {
    this.#scrollingLayer.clear();
    this.#fixedLayer?.clear();

    for (const options of this.#activeHighlights.values()) {
      this.#sync(options);
    }
  }

  /**
   * Removes both physical renderers owned by this logical layer.
   */
  dispose() {
    if (this.#disposed) {
      return;
    }

    this.#disposed = true;
    this.#layoutObserver?.disconnect();
    this.#layoutObserver = null;
    this.#activeHighlights.clear();
    this.#controller.removeLayer(this.#scrollingLayer);

    if (this.#fixedLayer) {
      this.#controller.removeLayer(this.#fixedLayer);
      this.#fixedLayer = null;
    }
  }

  #sync(options: AddHighlightOptions): HTMLElement {
    const fixedColumnCount = this.#getFixedColumnCount();
    const minCol = Math.min(options.range.anchor.col, options.range.lead.col);
    const maxCol = Math.max(options.range.anchor.col, options.range.lead.col);
    const canUseFixedSurface =
      fixedColumnCount > 0 &&
      (options.mode === 'cell' || options.mode === 'column') &&
      minCol < fixedColumnCount;

    if (!canUseFixedSurface) {
      this.#fixedLayer?.remove(options.range.id);
      this.#fixedLayer?.remove(this.#fixedFragmentId(options.range.id));

      return this.#scrollingLayer.add({
        ...options,
        zIndex: this.#getScrollingZIndex(options)
      });
    }

    const fixedLayer = this.#getFixedLayer();

    if (maxCol < fixedColumnCount) {
      this.#scrollingLayer.remove(options.range.id);
      fixedLayer.remove(this.#fixedFragmentId(options.range.id));

      return fixedLayer.add(options);
    }

    const scrollingRange: HighlightRange = {
      ...options.range,
      anchor: {
        ...options.range.anchor,
        col: Math.max(options.range.anchor.col, fixedColumnCount)
      },
      lead: {
        ...options.range.lead,
        col: Math.max(options.range.lead.col, fixedColumnCount)
      }
    };
    const scrollingElement = this.#scrollingLayer.add({
      ...options,
      range: scrollingRange,
      className: [options.className, HIGHLIGHT_SCROLLING_FRAGMENT_OPEN_START_CLASS]
        .filter(Boolean)
        .join(' '),
      zIndex: this.#getScrollingZIndex(options)
    });
    const fixedRange: HighlightRange = {
      ...options.range,
      id: this.#fixedFragmentId(options.range.id),
      anchor: {
        ...options.range.anchor,
        col: Math.min(options.range.anchor.col, fixedColumnCount - 1)
      },
      lead: {
        ...options.range.lead,
        col: Math.min(options.range.lead.col, fixedColumnCount - 1)
      }
    };

    fixedLayer.remove(options.range.id);
    fixedLayer.add({
      ...options,
      range: fixedRange,
      className: [options.className, HIGHLIGHT_FIXED_FRAGMENT_OPEN_END_CLASS]
        .filter(Boolean)
        .join(' ')
    });

    return scrollingElement;
  }

  #getFixedLayer(): HighlightsRenderer {
    if (!this.#fixedLayer) {
      this.#fixedLayer = this.#controller.createLayer({
        horizontalScroll: 'fixed',
        surfaceZIndex: FIXED_HIGHLIGHTS_SURFACE_Z_INDEX
      });
    }

    return this.#fixedLayer;
  }

  #getFixedColumnCount(): number {
    const boundary = this.#scrollContainer.querySelector(
      PINNED_LEADING_BOUNDARY_SELECTOR
    );

    if (!boundary) {
      return 0;
    }

    const boundaryColumnIndex = Number.parseInt(
      boundary.getAttribute('data-col-index') ?? '',
      10
    );

    return Number.isFinite(boundaryColumnIndex)
      ? Math.max(0, boundaryColumnIndex + 1)
      : 0;
  }

  #fixedFragmentId(id: string): string {
    return `${id}${FIXED_FRAGMENT_ID_SUFFIX}`;
  }

  #getScrollingZIndex(options: AddHighlightOptions): number {
    return options.mode === 'row' || options.mode === 'container'
      ? options.zIndex
      : this.#scrollingZIndex;
  }
}
